import type { ApiErrorBody } from '@/types/api';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    options?: { details?: unknown; requestId?: string },
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = options?.details;
    this.requestId = options?.requestId;
  }

  get displayMessage(): string {
    switch (this.code) {
      case 'UNAUTHORIZED':
      case 'INVALID_REFRESH_TOKEN':
        return 'Your session has expired. Please sign in again.';
      case 'VALIDATION_ERROR':
        return 'Some fields are invalid. Check your input and try again.';
      case 'DECK_QUOTA_EXCEEDED':
      case 'FREE_LIMIT_REACHED':
        return 'You have reached your free deck limit. Upgrade to create more decks.';
      default:
        return this.message;
    }
  }
}

export function parseApiErrorBody(body: unknown): ApiErrorBody['error'] | null {
  if (!body || typeof body !== 'object' || !('error' in body)) {
    return null;
  }

  const error = (body as ApiErrorBody).error;
  if (!error?.code || !error?.message) {
    return null;
  }

  return error;
}

export async function errorFromResponse(response: Response): Promise<ApiClientError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return new ApiClientError(
      response.status,
      'REQUEST_FAILED',
      response.statusText || 'Request failed',
    );
  }

  const parsed = parseApiErrorBody(body);
  if (parsed) {
    return new ApiClientError(response.status, parsed.code, parsed.message, {
      details: parsed.details,
      requestId: parsed.requestId,
    });
  }

  return new ApiClientError(
    response.status,
    'REQUEST_FAILED',
    response.statusText || 'Request failed',
  );
}
